import { element, elementsOf, globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';
import { addCss, hide, removeCss, show } from '@shared/easyfy.utils';
import basketService from '@webServices/basket.service';
import { AddToCartState } from '@shared/easyfy.enums';
import { animationEnded } from '@clientShared/utils';
import { SnackBar, SnackbarSeverity } from '@clientWebModules/snackbar';
import PriceHelper from '@clientShared/priceHelper';
import { SalesUnitEvents } from '@clientWebModules/salesunitselector';

@invoke
export default class Basket {
  static Instance: Basket;
  constructor() {
    Basket.Instance = this;
    globalEvent({
      type: 'click',
      events: [
        {
          action: 'add-to-cart',
          callback: this.onAddToCart,
        },
        {
          action: 'basketrow-change',
          callback: this.onBasketRowChange,
        },
        {
          action: 'basketrow-remove',
          callback: this.onBasketRowRemove,
        },
        {
          action: 'decrease-quantity',
          callback: this.onDecreaseQuantity,
        },
        {
          action: 'increase-quantity',
          callback: this.onIncreaseQuantity,
        },
      ],
    });

    globalEvent({
      type: 'change',
      event: {
        action: 'basketrow-set',
        callback: this.onBasketRowSet,
      },
    });
  }

  onInit() {}

  onAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const el: HTMLElement = e.target as HTMLElement;
    let quantity: number = 1;
    let interactionEl: HTMLElement = el.closest('.pp-interaction-container') as HTMLElement;
    let qtyEl = interactionEl?.querySelector('#quantity') as HTMLInputElement;

    if (qtyEl) {
      quantity = parseInt(qtyEl.value);
    }

    /* Change element state to 'processing'. */
    el.dataset.state = AddToCartState.Processing;
    el.setAttribute('disabled', 'disabled');

    if (el.querySelectorAll('.state').length === 0) {
      el.appendChild(document.createElement('div')).classList.add('state');
    }

    const headerBasket: HTMLElement = element('[data-container="header-basket"]') as HTMLElement;
    const hasSubscription = el.dataset?.optionid != null;
    const searchProductId = el.dataset?.searchproductid;
    let properties: { [key: string]: string } = null;
    if (el.dataset?.upsellid)
      properties = {
        [basketService.UpsellKey.UpsellId]: el.dataset?.upsellid,
      };

    await basketService
      .UpdateBasket(
        {
          rows: [
            {
              productId: el.dataset?.productid,
              salesUnitCode: el.dataset?.salesunitcode,
              changeQty: quantity,
              trackingData: {
                listName: el?.dataset.originatedFromListName,
                listId: el?.dataset.originatedFromListId,
                searchProductOrigin: searchProductId,
              },
              subscriptionOptions: {
                optionId: el?.dataset?.optionid,
              },
              properties: properties,
            },
          ],
        },
        el.dataset?.token
      )
      .then((response) => {
        Basket.Instance.renderBasket(response);
        if (response?.data?.message) {
          /*Default is error/fail.*/
          let elementState = AddToCartState.Fail;
          if (response.data.success === true) {
            elementState = AddToCartState.Success;
            if (hasSubscription) {
              this.updateSubscriptionFlyout(searchProductId);
            }

            addCss(headerBasket, 'bounce');
            animationEnded(headerBasket, () => {
              removeCss(headerBasket, 'bounce');
            });
          } else {
            SnackBar.appendSnackbar(SnackbarSeverity.error, response.data.message);
          }
          el.dataset.state = elementState;
        }
      })
      .catch(() => {
        /* Change element state to 'Fail'. */
        el.dataset.state = AddToCartState.Fail;
      });

    el.removeAttribute('disabled');

    /* Remove element state. */
    setTimeout(() => {
      el.removeAttribute('data-state');
      el.querySelector('.state').remove();
    }, 2000);

    const quantityElement: HTMLElement = headerBasket.querySelector('[data-container="basket-quantity"]') as HTMLElement;
    if (quantityElement.innerText === '1' && el.dataset.state != AddToCartState.Fail) {
      const closeSalesUnitSelector = new CustomEvent(SalesUnitEvents.Close);
      document.dispatchEvent(closeSalesUnitSelector);

      setTimeout(() => {
        headerBasket.click();
      }, 200);
    }

    return false;
  };

  onBasketRowChange = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const element: HTMLElement = e.target as HTMLElement;
    const basketButtons = element?.closest('[data-container="basket-buttons"') as HTMLElement;

    const rowId = basketButtons?.dataset?.rowid;
    const quantity = element.dataset?.add != null ? 1 : -1;
    const subscriptionOptionId = basketButtons.dataset?.subscriptionoptionid;
    let properties: { [key: string]: string } = null;
    if (basketButtons?.dataset?.upsellid)
      properties = {
        [basketService.UpsellKey.UpsellId]: basketButtons.dataset?.upsellid,
      };
    await basketService
      .UpdateBasket(
        {
          rows: [
            {
              productId: basketButtons.dataset?.productid,
              rowId: rowId,
              changeQty: quantity,
              subscriptionOptions: {
                optionId: subscriptionOptionId,
              },

              trackingData: {
                listName: basketButtons.dataset?.originatedFromListName,
                listId: basketButtons.dataset?.originatedFromListId,
                searchProductOrigin: basketButtons.dataset?.searchproductid,
              },
              properties: properties,
            },
          ],
        },
        basketButtons?.dataset['token']
      )
      .then((response) => {
        if (!response?.data?.success && response?.data?.message) SnackBar.appendSnackbar(SnackbarSeverity.error, response.data.message);

        Basket.Instance.renderBasket(response);
      })
      .catch(() => {});

    return false;
  };

  onBasketRowSet = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const element: HTMLInputElement = e.target as HTMLInputElement;
    const basketButtons = element?.closest('[data-container="basket-buttons"') as HTMLElement;
    const rowId = basketButtons?.dataset?.rowid;
    const quantity: number = parseInt(element.value);
    const subscriptionOptionId = basketButtons.dataset?.subscriptionoptionid;

    let properties: { [key: string]: string } = null;
    if (basketButtons?.dataset?.upsellid)
      properties = {
        [basketService.UpsellKey.UpsellId]: basketButtons.dataset?.upsellid,
      };

    await basketService
      .UpdateBasket(
        {
          rows: [
            {
              productId: basketButtons.dataset?.productid,
              rowId: rowId,
              setQty: quantity,
              subscriptionOptions: {
                optionId: subscriptionOptionId,
              },
              trackingData: {
                listName: basketButtons.dataset?.originatedFromListName,
                listId: basketButtons.dataset?.originatedFromListId,
                searchProductOrigin: basketButtons.dataset?.searchproductid,
              },
              properties: properties,
            },
          ],
        },
        basketButtons?.dataset.token
      )
      .then((response) => {
        if (!response?.data?.success && response?.data?.message) SnackBar.appendSnackbar(SnackbarSeverity.error, response.data.message);
        Basket.Instance.renderBasket(response);
      })
      .catch(() => {});

    return false;
  };

  onBasketRowRemove = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const element: HTMLElement = e.target as HTMLElement;
    const basketButtons = element?.closest('[data-container="basket-buttons"') as HTMLElement;

    await basketService
      .UpdateBasket(
        {
          rows: [
            {
              rowId: basketButtons?.dataset?.rowid,
              setQty: 0,
              trackingData: {
                listName: basketButtons.dataset?.originatedFromListName,
                listId: basketButtons.dataset?.originatedFromListId,
                searchProductOrigin: basketButtons.dataset?.searchproductid,
              },
            },
          ],
        },
        basketButtons.dataset.token
      )
      .then((response) => {
        if (!response?.data?.success && response?.data?.message) SnackBar.appendSnackbar(SnackbarSeverity.error, response.data.message);
        Basket.Instance.renderBasket(response);
      })
      .catch(() => {});

    return false;
  };

  onDecreaseQuantity() {
    Basket.Instance.changeQuantity(false);
    return false;
  }

  onIncreaseQuantity() {
    Basket.Instance.changeQuantity(true);
    return false;
  }

  static getBasket = async () => {
    await basketService
      .GetBasket()
      .then((response) => {
        Basket.Instance.renderBasket(response);
      })
      .catch(() => {});

    return false;
  };

  renderBasket = (response: any) => {
    if (response?.status == 200 && response?.data != null) {
      const basketContentEl: HTMLElement = element('.basket-content') as HTMLElement;
      const scrollPos = basketContentEl?.scrollTop;
      const headerBasket: HTMLElement = element('[data-container="header-basket"]') as HTMLElement;
      const miniBasket: HTMLElement = element('[data-container="mini-basket"]') as HTMLElement;
      const priceElement: HTMLElement = headerBasket.querySelector('[data-container="basket-price"]') as HTMLElement;
      const quantityElement: HTMLElement = headerBasket.querySelector('[data-container="basket-quantity"]') as HTMLElement;
      const headerCheckout: HTMLElement = document.querySelector('[data-container="header-checkout"]') as HTMLElement;

      if (response?.data?.totalQuantity > 0) {
        if (priceElement) {
          priceElement.innerHTML = response?.data?.prices?.priceIncVatExclDiscountExclShipping;
          //show(priceElement);
        }

        if (quantityElement) {
          quantityElement.innerHTML = response?.data?.totalQuantity;
          show(quantityElement, 'flex');
        }
        if (headerCheckout) {
          headerCheckout.removeAttribute('disabled');
        }
      } else {
        if (quantityElement) {
          hide(quantityElement);
        }

        if (priceElement) {
          //hide(priceElement);
          priceElement.innerHTML = priceElement.dataset.basketText;
        }

        if (headerCheckout) {
          headerCheckout.setAttribute('disabled', 'disabled');
        }
      }

      if (miniBasket) {
        miniBasket.innerHTML = response.data.partial;

        const basketContentElNew: HTMLElement = element('.basket-content') as HTMLElement;
        if (scrollPos > 0 && basketContentElNew.scrollTop != scrollPos) {
          basketContentElNew.scrollTo(0, scrollPos);
        }
      }
    }

    return false;
  };

  private changeQuantity = (increase: boolean) => {
    const parentContainer = element('[data-container="page"]');
    const quantityInput = elementsOf(parentContainer, '#quantity') as HTMLInputElement;
    const priceElement: HTMLElement = elementsOf(parentContainer, '.pp-actions .price') as HTMLElement;
    const price = priceElement?.getAttribute('data-sales-price');
    const unitPrice = parseFloat(price?.replace(',', '.'));
    const locale = document?.documentElement.getAttribute('data-culture');

    let quantity = 0;

    if (quantityInput) {
      if (increase) {
        quantityInput.stepUp();
      } else {
        quantityInput.stepDown();
      }
      quantity = +quantityInput.value;
    }

    if (priceElement && unitPrice && quantity) {
      const newPrice = PriceHelper.updateAndFormatPrice(priceElement.innerText, unitPrice * quantity, locale);
      if (newPrice) {
        priceElement.innerText = newPrice;
      }
    }

    return false;
  };

  private updateSubscriptionFlyout = (productId: string) => {
    const flyoutCloseBtn = element(`[data-target="product-options-selector-subscription-${productId}"]`) as HTMLElement;
    flyoutCloseBtn?.click();

    const freqSelector = element('[data-container="subscription-freq"]') as HTMLElement;
    const updateFreqFlyout = freqSelector?.dataset?.updateflyout;

    if (updateFreqFlyout && updateFreqFlyout.toLowerCase() == 'true') {
      const updateFlyoutEvent = new CustomEvent('update-subs-flyout', { bubbles: true, cancelable: false });
      document.dispatchEvent(updateFlyoutEvent);
    }
  };
}
